/**
 * Générateur — Effectuer la division euclidienne d'un entier par un entier < 100
 * Attendu ID : 618 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 7 EXERCICES DIDACTIQUEMENT DISTINCTS (chacun avec
 * son propre generatorKey), certains déclinés en plusieurs formats UI :
 *
 *   1. `poser_diviseur_simple`     → QCM unique + Saisie
 *      Poser et effectuer a ÷ b avec b ∈ [2, 9] (reprise CM).
 *
 *   2. `poser_diviseur_2chiffres`  → QCM unique + Saisie
 *      Poser et effectuer a ÷ b avec b ∈ [10, 99] (extension 6ème, table de multiples).
 *
 *   3. `lecture_egalite`           → QCM unique + Saisie
 *      À partir de a = b×q + r, donner (q, r) d'une division euclidienne.
 *
 *   4. `mixte_fraction`            → Saisie uniquement
 *      Écrire a/b sous forme de nombre mixte q + r/b.
 *
 *   5. `encadrement_fraction`      → QCM unique + Saisie
 *      Encadrer a/b par deux entiers consécutifs q < a/b < q+1.
 *
 *   6. `relier_divisions`          → Relier
 *      Associer 4 divisions euclidiennes à leurs couples (q ; r).
 *
 *   7. `vrai_faux_euclidienne`     → Vrai/Faux
 *      Juger 4 égalités a = b×q + r (2V/2F).
 *
 * Règles dures de la fiche cluster 10 (attendu 618) :
 *   - Dividende a ∈ [10, 99999], diviseur b ∈ [2, 99], b > 0.
 *   - Tous les triplets (a, b, q, r) sont vérifiés par isValidEucl avant émission.
 *   - Le quotient doit être ≥ 1 ; pour les fractions (C), r ≥ 1 obligatoire.
 *   - Réponses toujours entières : les saisies refusent virgule/point.
 *
 * Les 4 grandes familles d'erreur (distracteurs) :
 *   1. Reste ≥ diviseur    → (q-1, r+b)
 *   2. Quotient surévalué  → (q+1, r-b) si valide, sinon (q+1, 0)
 *   3. Oubli du reste      → (q, 0)
 *   4. Quotient décimal    → "a/b ≈ x,y"
 *   5. Couple inversé      → (r, q)
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 618;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_POSER_SIMPLE_QCM   = 'Diviseur < 10 — poser la division euclidienne (QCM)';
const L_POSER_SIMPLE_SAI   = 'Diviseur < 10 — poser la division euclidienne (Saisie)';
const L_POSER_2CHIFFRES_QCM = 'Diviseur ∈ [10, 99] — poser la division euclidienne (QCM)';
const L_POSER_2CHIFFRES_SAI = 'Diviseur ∈ [10, 99] — poser la division euclidienne (Saisie)';
const L_LECTURE_QCM        = 'Lecture d\'une égalité a = b×q + r (QCM)';
const L_LECTURE_SAI        = 'Lecture d\'une égalité a = b×q + r (Saisie)';
const L_MIXTE              = 'Écriture d\'une fraction en nombre mixte (Saisie)';
const L_ENCADREMENT_QCM    = 'Encadrement d\'une fraction par deux entiers (QCM)';
const L_ENCADREMENT_SAI    = 'Encadrement d\'une fraction par deux entiers (Saisie)';
const L_RELIER             = 'Relier 4 divisions à leurs couples (quotient ; reste)';
const L_VF                 = 'Vrai/Faux : 4 égalités a = b×q + r';

// Rappels affichés en tête des énoncés (revue 2026-08-24, choix prof, même
// principe que les gens 613/616/617). Pas de rappel sur lecture_egalite :
// l'égalité y est DONNÉE, il n'y a rien à poser.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pense à poser la division sur ton cahier ou au brouillon avant de répondre.</p>';
const RAPPEL_VERIF = '<p class="italic text-slate-400">✏️ Pense à vérifier les calculs sur ton cahier ou au brouillon.</p>';

// ═══════════════════════════════════════════════════════════
// HELPERS ARITHMÉTIQUES (spécifiques à la division euclidienne)
// ═══════════════════════════════════════════════════════════

/** Division euclidienne d'entiers : retourne { q, r }. */
const divEucl = (a, b) => {
    if (!Number.isInteger(a) || !Number.isInteger(b) || b <= 0) {
        throw new Error(`divEucl: arguments invalides a=${a}, b=${b}`);
    }
    return { q: Math.floor(a / b), r: a % b };
};

/** Vérifie qu'un quadruplet (a, b, q, r) est une division euclidienne valide. */
const isValidEucl = (a, b, q, r) =>
    Number.isInteger(a) && Number.isInteger(b) && Number.isInteger(q) && Number.isInteger(r)
    && b > 0 && q >= 0 && r >= 0 && r < b && q * b + r === a;

/** Arrondi au supérieur : pour les problèmes de transport (cluster 619, utilisé aussi ici). */
const ceilDiv = (a, b) => Math.ceil(a / b);

/** Approximation décimale à 2 chiffres pour distracteur « quotient décimal ». */
const toDecApprox = (a, b) => {
    const raw = a / b;
    return raw.toFixed(2).replace('.', ',');
};

/**
 * Nettoyage des distracteurs :
 * - supprime les doublons (par chaîne)
 * - retire ceux qui sont égaux à la bonne réponse
 * - garantit qu'il y a `n` distracteurs en complétant via `fallbackFn(i)`
 */
const cleanDistracteurs = (bonne, dists, n, fallbackFn) => {
    const out = [];
    const seen = new Set([String(bonne)]);
    for (const d of dists) {
        const s = String(d);
        if (!seen.has(s)) { out.push(d); seen.add(s); }
    }
    let guard = 0;
    while (out.length < n && guard < 40) {
        const cand = fallbackFn(guard);
        const s = String(cand);
        if (!seen.has(s)) { out.push(cand); seen.add(s); }
        guard++;
    }
    return out.slice(0, n);
};

/** Format texte d'un couple (q, r) pour les QCM. */
const formatCouple = (q, r) => `quotient = ${q} et reste = ${r}`;

// ═══════════════════════════════════════════════════════════
// POOLS DE TRIPLETS VALIDÉS
// ═══════════════════════════════════════════════════════════

/**
 * Pool A — diviseur < 10. Tous vérifiés : q × b + r = a et 0 ≤ r < b.
 * Couverture : restes = 0 ET restes > 0, dividendes 2-4 chiffres.
 */
const POOL_A = [
    [87, 4, 21, 3],
    [158, 7, 22, 4],
    [165, 3, 55, 0],
    [219, 4, 54, 3],
    [245, 6, 40, 5],
    [432, 5, 86, 2],
    [534, 8, 66, 6],
    [73, 6, 12, 1],
    [95, 6, 15, 5],
    [48, 5, 9, 3],
    [63, 4, 15, 3],
    [200, 3, 66, 2],
    [75, 4, 18, 3],
    [125, 7, 17, 6],
    [307, 9, 34, 1],
    [841, 6, 140, 1],
    [1237, 6, 206, 1],
    [2458, 7, 351, 1],
    [9855, 7, 1407, 6],
    [3456, 8, 432, 0],
    [1729, 5, 345, 4],
    [578, 9, 64, 2],
    [649, 3, 216, 1],
    [483, 7, 69, 0],
    [100, 6, 16, 4],
    [50, 7, 7, 1],
    [81, 5, 16, 1],
    [144, 8, 18, 0],
    [231, 4, 57, 3],
    [365, 6, 60, 5],
];

/**
 * Pool B — diviseur ∈ [10, 99], quotient ≥ 2. Tous vérifiés.
 * Couverture : algorithme à plusieurs descentes.
 */
const POOL_B = [
    [686, 15, 45, 11],
    [295, 18, 16, 7],
    [536, 45, 11, 41],
    [958, 17, 56, 6],
    [390, 14, 27, 12],
    [6798, 55, 123, 33],
    [2647, 40, 66, 7],
    [452, 13, 34, 10],
    [800, 22, 36, 8],
    [1200, 35, 34, 10],
    [3500, 48, 72, 44],
    [5432, 21, 258, 14],
    [450, 12, 37, 6],
    [777, 25, 31, 2],
    [1234, 19, 64, 18],
    [2500, 33, 75, 25],
    [4321, 50, 86, 21],
    [612, 16, 38, 4],
    [987, 24, 41, 3],
    [1500, 27, 55, 15],
    [3000, 42, 71, 18],
    [178, 11, 16, 2],
    [365, 20, 18, 5],
    [672, 30, 22, 12],
    [856, 37, 23, 5],
    [1980, 60, 33, 0],
    [2401, 49, 49, 0],
    [5000, 75, 66, 50],
    [729, 17, 42, 15],
    [1456, 26, 56, 0],
];

/**
 * Pool C — pour fractions mixtes et encadrements : r ≥ 1, b ≤ 20, q < 50.
 */
const POOL_C = [
    [87, 4, 21, 3],
    [50, 7, 7, 1],
    [100, 9, 11, 1],
    [73, 6, 12, 1],
    [200, 11, 18, 2],
    [43, 5, 8, 3],
    [59, 8, 7, 3],
    [125, 12, 10, 5],
    [95, 7, 13, 4],
    [163, 15, 10, 13],
    [33, 4, 8, 1],
    [47, 6, 7, 5],
    [65, 9, 7, 2],
    [112, 13, 8, 8],
    [77, 10, 7, 7],
    [138, 16, 8, 10],
    [55, 8, 6, 7],
    [182, 20, 9, 2],
    [71, 5, 14, 1],
    [29, 3, 9, 2],
    [85, 6, 14, 1],
    [104, 9, 11, 5],
    [156, 17, 9, 3],
    [199, 18, 11, 1],
    [222, 19, 11, 13],
];

/**
 * Pool D — pour `lecture_egalite` : contrainte r < min(b, q) pour que
 * l'égalité permette les DEUX lectures (a÷b et a÷q).
 */
const POOL_D = [
    [87, 4, 21, 3],
    [116, 7, 16, 4],
    [50, 7, 7, 1],
    [73, 6, 12, 1],
    [100, 9, 11, 1],
    [200, 11, 18, 2],
    [59, 8, 7, 3],
    [85, 6, 14, 1],
    [125, 12, 10, 5],
    [165, 8, 20, 5],
    [245, 20, 12, 5],
    [178, 11, 16, 2],
    [307, 9, 34, 1],
    [432, 5, 86, 2],
    [219, 4, 54, 3],
    [95, 6, 15, 5],
    [63, 4, 15, 3],
    [48, 5, 9, 3],
    [33, 4, 8, 1],
    [29, 3, 9, 2],
];

// Filtrage préventif : on écarte tout triplet qui ne respecte pas isValidEucl.
// Pool D doit en plus respecter r < min(b, q).
const POOL_A_V = POOL_A.filter(([a, b, q, r]) => isValidEucl(a, b, q, r));
const POOL_B_V = POOL_B.filter(([a, b, q, r]) => isValidEucl(a, b, q, r) && q >= 2);
const POOL_C_V = POOL_C.filter(([a, b, q, r]) => isValidEucl(a, b, q, r) && r >= 1 && b <= 20 && q < 50);
const POOL_D_V = POOL_D.filter(([a, b, q, r]) => isValidEucl(a, b, q, r) && r < Math.min(b, q));

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — poser_diviseur_simple (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

// Tirage DYNAMIQUE (revue 2026-08-24, choix prof) : le pool figé de 30
// triplets plafonnait à 90 énoncés (58 répétitions immédiates / 5000).
// b ∈ [2, 9], a ∈ [10, 9999] → (q, r) calculés, toujours valides par
// construction. Les retombées à reste nul arrivent naturellement (~1/b).
const pickPoserSimple = () => {
    const b = randInt(2, 9);
    // Revue 2026-08-25 (choix prof) : 70 % des dividendes < 1000 (2-3 chiffres,
    // le cœur de la reprise CM), 30 % à 4 chiffres pour l'extension.
    const a = Math.random() < 0.7 ? randInt(10, 999) : randInt(1000, 9999);
    const { q, r } = divEucl(a, b);
    return [a, b, q, r];
};

const buildEnoncePoserSimple = (a, b) => {
    const formulations = [
        `<p>Effectue la division euclidienne de ${a} par ${b}. Donne le quotient et le reste.</p>`,
        `<p>Pose et effectue la division euclidienne de ${a} par ${b}.</p>`,
        `<p>Quel est le quotient et le reste de la division euclidienne de ${a} par ${b} ?</p>`,
    ];
    return RAPPEL_BROUILLON + randomChoice(formulations);
};

// Anti-répétition immédiate (revue 2026-08-24, recette standard).
let derniereSignaturePoserSimpleQcm618 = null;

const genPoserSimpleQcm = () => {
    let payload = construirePoserSimpleQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignaturePoserSimpleQcm618; t++) {
        payload = construirePoserSimpleQcm();
    }
    derniereSignaturePoserSimpleQcm618 = payload.texte;
    return payload;
};

const construirePoserSimpleQcm = () => {
    const [a, b, q, r] = pickPoserSimple();
    const bonne = formatCouple(q, r);

    // Distracteurs fiche §6 exo A :
    //  D1 = (q-1, r+b) reste ≥ diviseur
    //  D2 = (q+1, r-b) si valide quotient surévalué
    //  D3 = (q, 0) oubli du reste (si r>0)
    //  D4 = (r, q) couple inversé
    //  D5 = quotient décimal (tronc 2)
    // Revue 2026-08-24 (choix prof) : 4 choix comme le reste de la campagne,
    // et distracteur « quotient ≈ x,yz » retiré — seul choix avec un ≈ et une
    // virgule, il se repérait à sa forme dans 100 % des tirages (A3).
    const candidats = [];
    candidats.push(formatCouple(q - 1, r + b));   // reste ≥ diviseur (arrêt prématuré)
    if (r > 0) candidats.push(formatCouple(q, 0)); // oubli du reste
    candidats.push(formatCouple(r, q));            // couple inversé

    const distracteurs = cleanDistracteurs(bonne, candidats, 3, (i) => {
        if (i === 0) return formatCouple(q + 1, 0);
        if (i === 1) return formatCouple(q, r + 1);
        if (i === 2) return formatCouple(q - 1, r);
        return formatCouple(q + 2, 0);
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(choix, bonne, buildEnoncePoserSimple(a, b), opts(L_POSER_SIMPLE_QCM));
    payload._meta.generatorKey = 'poser_diviseur_simple';
    return payload;
};

// Anti-répétition immédiate (revue 2026-08-24, recette standard).
let derniereSignaturePoserSimpleSai618 = null;

const genPoserSimpleSaisie = () => {
    let payload = construirePoserSimpleSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignaturePoserSimpleSai618; t++) {
        payload = construirePoserSimpleSaisie();
    }
    derniereSignaturePoserSimpleSai618 = payload.texte;
    return payload;
};

const construirePoserSimpleSaisie = () => {
    const [a, b, q, r] = pickPoserSimple();
    const phrase = `quotient = {{${q}}} et reste = {{${r}}}`;
    const payload = makeTexteTrou(
        phrase,
        buildEnoncePoserSimple(a, b),
        { ...opts(L_POSER_SIMPLE_SAI), mode: 'saisie', strict: false },
    );
    payload._meta.generatorKey = 'poser_diviseur_simple';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — poser_diviseur_2chiffres (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

// Tirage DYNAMIQUE (revue 2026-08-26, choix prof) : diviseur à 2 chiffres,
// dividende < 1000 dans 70 % des cas (30 % à 4 chiffres). Remplace le pool
// figé de 30 triplets. q >= 2 garanti (a >= 2b).
const pickPoser2ch = () => {
    const b = randInt(10, 99);
    const petit = Math.random() < 0.7;
    const min = Math.max(2 * b, petit ? 100 : 1000);
    const max = petit ? 999 : 9999;
    const a = randInt(min, max);
    const { q, r } = divEucl(a, b);
    return [a, b, q, r];
};

const buildEnoncePoser2ch = (a, b) => {
    const formulations = [
        `<p>Effectue la division euclidienne de ${a} par ${b}. Tu peux t'aider d'une table de multiples de ${b} si besoin.</p>`,
        `<p>Pose et effectue la division euclidienne de ${a} par ${b}. Donne le quotient et le reste.</p>`,
        `<p>Quel est le quotient et le reste de la division euclidienne de ${a} par ${b} ? (Tu peux dresser une table de multiples de ${b}.)</p>`,
    ];
    return RAPPEL_BROUILLON + randomChoice(formulations);
};

// Anti-répétition immédiate (revue 2026-08-26, recette standard).
let derniereSignaturePoser2chQcm618 = null;

const genPoser2chQcm = () => {
    let payload = construirePoser2chQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignaturePoser2chQcm618; t++) {
        payload = construirePoser2chQcm();
    }
    derniereSignaturePoser2chQcm618 = payload.texte;
    return payload;
};

const construirePoser2chQcm = () => {
    const [a, b, q, r] = pickPoser2ch();
    const bonne = formatCouple(q, r);

    const candidats = [];
    candidats.push(formatCouple(q - 1, r + b));
    const r2 = r - b;
    if (q + 1 >= 0 && r2 >= 0) candidats.push(formatCouple(q + 1, r2));
    if (r > 0) candidats.push(formatCouple(q, 0));
    candidats.push(formatCouple(r, q));
    // Distracteur « table mal lue » : quotient ± 1 unité
    candidats.push(formatCouple(q + 1, r));

    const distracteurs = cleanDistracteurs(bonne, candidats, 4, (i) => {
        if (i === 0) return `quotient ≈ ${toDecApprox(a, b)} et reste = 0`;
        if (i === 1) return formatCouple(q - 1, r);
        if (i === 2) return formatCouple(q, r + 1);
        return formatCouple(q + 2, Math.max(0, r - 1));
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(choix, bonne, buildEnoncePoser2ch(a, b), opts(L_POSER_2CHIFFRES_QCM));
    // Calculatrice RETIRÉE (revue 2026-08-26, choix prof) : le BO ne
    // l'autorise pas pour la technique posée.
    payload._meta.generatorKey = 'poser_diviseur_2chiffres';
    return payload;
};

// Anti-répétition immédiate (revue 2026-08-26, recette standard).
let derniereSignaturePoser2chSai618 = null;

const genPoser2chSaisie = () => {
    let payload = construirePoser2chSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignaturePoser2chSai618; t++) {
        payload = construirePoser2chSaisie();
    }
    derniereSignaturePoser2chSai618 = payload.texte;
    return payload;
};

const construirePoser2chSaisie = () => {
    const [a, b, q, r] = pickPoser2ch();
    const phrase = `quotient = {{${q}}} et reste = {{${r}}}`;
    const payload = makeTexteTrou(
        phrase,
        buildEnoncePoser2ch(a, b),
        { ...opts(L_POSER_2CHIFFRES_SAI), mode: 'saisie', strict: false },
    );
    // Calculatrice RETIRÉE (revue 2026-08-26, choix prof) : cf. QCM.
    payload._meta.generatorKey = 'poser_diviseur_2chiffres';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — lecture_egalite (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

// Tirage DYNAMIQUE lecture_egalite (revue 2026-08-26, choix prof) : le pool
// figé de 20 triplets saturait (9 doublons / 40 au harnais). Contrainte du
// format : r < min(b, q) pour que l'égalité a = b × q + r permette les DEUX
// lectures (division par b ou par q).
const pickLectureTriple = () => {
    // Répartition 70/30 (revue 2026-08-26, choix prof) : 70 % de dividendes
    // < 1000, 30 % à 4 chiffres (quotients plus grands, mêmes contraintes).
    const grand = Math.random() >= 0.7;
    for (let t = 0; t < 30; t++) {
        const b = randInt(3, 20);
        const q = grand ? randInt(Math.ceil(1000 / b), Math.floor(9999 / b)) : randInt(Math.max(3, b - 8), b + 15);
        if (q < 2) continue;
        const r = randInt(1, Math.min(b, q) - 1);
        const a = b * q + r;
        if (grand ? (a < 1000 || a > 9999) : a > 999) continue;
        if (isValidEucl(a, b, q, r) && r < Math.min(b, q)) return [a, b, q, r];
    }
    return randomChoice(POOL_D_V); // repli : pool historique
};

const buildEnonceLecture = (a, b, q, r, diviseur) => {
    // diviseur ∈ {b, q} : l'égalité a = b×q + r permet les deux lectures
    const formulations = [
        `<p>On sait que ${a} = ${b} × ${q} + ${r}. Quels sont le quotient et le reste de la division euclidienne de ${a} par ${diviseur} ?</p>`,
        `<p>L'égalité ${a} = ${b} × ${q} + ${r} est vraie. En déduire le quotient et le reste de la division euclidienne de ${a} par ${diviseur}.</p>`,
        `<p>Sachant que ${a} = ${b} × ${q} + ${r}, donne le quotient et le reste de ${a} divisé par ${diviseur}.</p>`,
    ];
    return randomChoice(formulations);
};

const pickLectureCouple = () => {
    const [a, b, q, r] = pickLectureTriple();
    // Tirer l'une des deux lectures possibles :
    //  - diviseur = b → (q_out, r_out) = (q, r)
    //  - diviseur = q → (q_out, r_out) = (b, r)  (car a = q×b + r aussi)
    const lectureSurB = Math.random() < 0.5;
    const diviseur = lectureSurB ? b : q;
    const qOut = lectureSurB ? q : b;
    const rOut = r;
    return { a, b, q, r, diviseur, qOut, rOut, lectureSurB };
};

// Anti-répétition immédiate (revue 2026-08-26, recette standard).
let derniereSignatureLectureQcm618 = null;

const genLectureQcm = () => {
    let payload = construireLectureQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureLectureQcm618; t++) {
        payload = construireLectureQcm();
    }
    derniereSignatureLectureQcm618 = payload.texte;
    return payload;
};

const construireLectureQcm = () => {
    const { a, b, q, r, diviseur, qOut, rOut, lectureSurB } = pickLectureCouple();
    const bonne = formatCouple(qOut, rOut);

    // Distracteurs :
    //  D1 = couple inversé (r, q_out)
    //  D2 = lecture sur l'AUTRE division (piège « je lis à côté »)
    //  D3 = (q_out + 1, r_out - diviseur) si valide, sinon (q_out + 1, 0)
    //  D4 = (q_out - 1, r_out + diviseur)
    const autreQ = lectureSurB ? b : q;
    const candidats = [
        formatCouple(rOut, qOut),                      // D1
        formatCouple(autreQ, rOut),                    // D2 mauvaise division
        formatCouple(qOut + 1, Math.max(0, rOut - diviseur)), // D3
        formatCouple(Math.max(0, qOut - 1), rOut + diviseur), // D4
    ];

    const distracteurs = cleanDistracteurs(bonne, candidats, 4, (i) => {
        if (i === 0) return formatCouple(qOut, 0);
        if (i === 1) return formatCouple(qOut + 2, rOut);
        if (i === 2) return formatCouple(rOut, diviseur);
        return formatCouple(diviseur, qOut);
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(choix, bonne, buildEnonceLecture(a, b, q, r, diviseur), opts(L_LECTURE_QCM));
    payload._meta.generatorKey = 'lecture_egalite';
    return payload;
};

// Anti-répétition immédiate (revue 2026-08-26, recette standard).
let derniereSignatureLectureSai618 = null;

const genLectureSaisie = () => {
    let payload = construireLectureSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureLectureSai618; t++) {
        payload = construireLectureSaisie();
    }
    derniereSignatureLectureSai618 = payload.texte;
    return payload;
};

const construireLectureSaisie = () => {
    const { a, b, q, r, diviseur, qOut, rOut } = pickLectureCouple();
    const phrase = `quotient = {{${qOut}}} et reste = {{${rOut}}}`;
    const payload = makeTexteTrou(
        phrase,
        buildEnonceLecture(a, b, q, r, diviseur),
        { ...opts(L_LECTURE_SAI), mode: 'saisie', strict: false },
    );
    payload._meta.generatorKey = 'lecture_egalite';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 4 — mixte_fraction (Saisie uniquement)
// ═══════════════════════════════════════════════════════════

const buildEnonceMixte = (a, b) => {
    const formulations = [
        `<p>Écris la fraction ${a}/${b} sous la forme d'un nombre entier plus une fraction inférieure à 1. Complète : ${a}/${b} = …… + ……/${b}.</p>`,
        `<p>Décompose la fraction ${a}/${b} en un nombre entier plus une fraction dont le numérateur est plus petit que le dénominateur. Complète : ${a}/${b} = …… + ……/${b}.</p>`,
        `<p>La fraction ${a}/${b} peut s'écrire sous la forme d'un entier suivi d'une fraction plus petite que 1. Complète : ${a}/${b} = …… + ……/${b}.</p>`,
    ];
    return RAPPEL_BROUILLON + randomChoice(formulations);
};

const genMixteSaisie = () => {
    const [a, b, q, r] = randomChoice(POOL_C_V);
    const phrase = `${a}/${b} = {{${q}}} + {{${r}}}/${b}`;
    const payload = makeTexteTrou(
        phrase,
        buildEnonceMixte(a, b),
        { ...opts(L_MIXTE), mode: 'saisie', strict: false },
    );
    payload._meta.generatorKey = 'mixte_fraction';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 5 — encadrement_fraction (QCM + Saisie)
// ═══════════════════════════════════════════════════════════

const buildEnonceEncadrement = (a, b) => {
    const formulations = [
        `<p>Encadre la fraction ${a}/${b} par deux entiers consécutifs. Complète : …… < ${a}/${b} < …… .</p>`,
        `<p>Donne les deux entiers consécutifs qui encadrent la fraction ${a}/${b}. Complète : …… < ${a}/${b} < …… .</p>`,
        `<p>Trouve les deux entiers consécutifs entre lesquels se situe la fraction ${a}/${b}. Complète : …… < ${a}/${b} < …… .</p>`,
    ];
    return RAPPEL_BROUILLON + randomChoice(formulations);
};

const genEncadrementQcm = () => {
    const [a, b, q, r] = randomChoice(POOL_C_V); // r≥1 garanti par POOL_C_V
    const bonne = `${q} < ${a}/${b} < ${q + 1}`;

    // Distracteurs fiche §6 exo E :
    //  D1 = (q-1, q)       décalage d'un rang inférieur
    //  D2 = (q, q+2)       trop large
    //  D3 = (b, b+1)       lecture inversée (le diviseur lu comme quotient)
    //  D4 = (0, q)         erreur triviale
    const candidats = [
        `${q - 1} < ${a}/${b} < ${q}`,
        `${q} < ${a}/${b} < ${q + 2}`,
        `${b} < ${a}/${b} < ${b + 1}`,
        `0 < ${a}/${b} < ${q}`,
    ];

    const distracteurs = cleanDistracteurs(bonne, candidats, 4, (i) => {
        if (i === 0) return `${q + 1} < ${a}/${b} < ${q + 2}`;
        if (i === 1) return `${r} < ${a}/${b} < ${r + 1}`;
        if (i === 2) return `${q - 1} < ${a}/${b} < ${q + 1}`;
        return `${q} < ${a}/${b} < ${q + 3}`;
    });

    const choix = [bonne, ...distracteurs];
    const payload = makeQcm(choix, bonne, buildEnonceEncadrement(a, b), opts(L_ENCADREMENT_QCM));
    payload._meta.generatorKey = 'encadrement_fraction';
    return payload;
};

const genEncadrementSaisie = () => {
    const [a, b, q, r] = randomChoice(POOL_C_V);
    const phrase = `{{${q}}} < ${a}/${b} < {{${q + 1}}}`;
    const payload = makeTexteTrou(
        phrase,
        buildEnonceEncadrement(a, b),
        { ...opts(L_ENCADREMENT_SAI), mode: 'saisie', strict: false },
    );
    payload._meta.generatorKey = 'encadrement_fraction';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 6 — relier_divisions (Relier uniquement)
// ═══════════════════════════════════════════════════════════

const genRelierDivisions = () => {
    // On tire 4 triplets distincts (majoritairement pool B, 1-2 de pool A)
    // en garantissant que les 4 couples (q, r) sont deux à deux distincts.
    const candidats = shuffle([...POOL_B_V, ...POOL_A_V]);
    const choisis = [];
    const couplesVus = new Set();
    for (const t of candidats) {
        const [a, b, q, r] = t;
        const key = `${q}|${r}`;
        if (couplesVus.has(key)) continue;
        // Éviter deux triplets partageant le même dividende a (ambiguïté visuelle)
        if (choisis.some((c) => c[0] === a)) continue;
        choisis.push(t);
        couplesVus.add(key);
        if (choisis.length === 4) break;
    }
    if (choisis.length < 4) return buildRelierFallback();

    const pairs = choisis.map(([a, b, q, r]) => ({
        source: `${a} ÷ ${b}`,
        target: `q = ${q} et r = ${r}`,
    }));

    const payload = makeRelier(
        pairs,
        `${RAPPEL_BROUILLON}<p>Relie chaque division euclidienne à son quotient et son reste.</p>`,
        opts(L_RELIER),
    );
    // Calculatrice RETIRÉE (revue 2026-08-26, choix prof) : le BO ne
    // l'autorise pas pour la technique posée (cf. poser_diviseur_2chiffres).
    payload._meta.generatorKey = 'relier_divisions';
    return payload;
};

const buildRelierFallback = () => {
    const pairs = [
        { source: '87 ÷ 4',   target: 'q = 21 et r = 3' },
        { source: '295 ÷ 18', target: 'q = 16 et r = 7' },
        { source: '158 ÷ 7',  target: 'q = 22 et r = 4' },
        { source: '686 ÷ 15', target: 'q = 45 et r = 11' },
    ];
    const payload = makeRelier(
        pairs,
        `${RAPPEL_BROUILLON}<p>Relie chaque division euclidienne à son quotient et son reste.</p>`,
        opts(L_RELIER),
    );
    // Calculatrice RETIRÉE (revue 2026-08-26, choix prof) : le BO ne
    // l'autorise pas pour la technique posée (cf. poser_diviseur_2chiffres).
    payload._meta.generatorKey = 'relier_divisions';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 7 — vrai_faux_euclidienne (V/F uniquement)
// ═══════════════════════════════════════════════════════════

const genVraiFauxEuclidienne = () => {
    // 4 lignes : 2 vraies + 2 fausses (2 familles d'erreur piochées parmi 3) :
    //   F1 : reste ≥ diviseur (arrêt prématuré, mais arithmétiquement vrai)
    //   F2 : erreur de calcul (q × b + r ≠ a)
    //   F3 : rôles échangés (q et r inversés)
    const all = shuffle([...POOL_A_V, ...POOL_B_V]).filter(([a, b, q, r]) => r > 0 && q > 0);
    if (all.length < 4) return buildVFFallback();

    // Quota VARIABLE de vraies (revue 2026-08-26) : 1 à 3 sur 4 lignes — le
    // 2V/2F constant était comptable.
    const nbVraies = randInt(1, 3);
    const vrais = all.slice(0, nbVraies);
    const fauxBases = all.slice(nbVraies, nbVraies + 3);
    // Revue 2026-08-26 (A1 attrapé par le prof) : le DIVISEUR est nommé dans
    // chaque ligne — sans lui, « 73 = 6 × 11 + 7 » lue avec 11 comme diviseur
    // est une division euclidienne correcte (quotient 6, reste 7 < 11), et
    // l'étiquette « Fausse » comptait faux un élève qui raisonnait juste.
    const V = vrais.map(([a, b, q, r]) => `Division de ${a} par ${b} : ${a} = ${b} × ${q} + ${r}`);

    // Piocher les familles d'erreur (shuffle pour varier l'angle testé)
    const [a1, b1, q1, r1] = fauxBases[0];
    const [a2, b2, q2, r2] = fauxBases[1];
    const [a3, b3, q3, r3] = fauxBases[2] || fauxBases[0];

    const familles = shuffle([
        // F1 : reste trop grand (arithmétiquement vrai, mais r ≥ b)
        // b × (q-1) + (r+b) = bq − b + r + b = bq + r = a ✓
        () => {
            const ligne = `Division de ${a1} par ${b1} : ${a1} = ${b1} × ${q1 - 1} + ${r1 + b1}`;
            const ok = b1 * (q1 - 1) + (r1 + b1) === a1 && (r1 + b1) >= b1;
            return ok ? ligne : null;
        },
        // F2 : erreur de calcul (égalité arithmétiquement fausse, +2 pour être visible)
        () => {
            const ligne = `Division de ${a3} par ${b3} : ${a3} = ${b3} × ${q3} + ${r3 + 2}`;
            const ok = b3 * q3 + (r3 + 2) !== a3;
            return ok ? ligne : null;
        },
        // F3 : rôles échangés (q et r inversés, presque sûrement faux numériquement)
        () => {
            const ligne = `Division de ${a2} par ${b2} : ${a2} = ${r2} × ${q2} + ${b2}`;
            const ok = r2 * q2 + b2 !== a2;
            return ok ? ligne : null;
        },
    ]);

    const nbFausses = 4 - nbVraies;
    const fausses = [];
    for (const famille of familles) {
        if (fausses.length >= nbFausses) break;
        const ligne = famille();
        if (ligne && !fausses.includes(ligne)) fausses.push(ligne);
    }
    if (fausses.length < nbFausses) return buildVFFallback();

    const indexed = [
        ...V.map((l) => ({ l, br: 0 })),
        ...fausses.map((l) => ({ l, br: 1 })),
    ];
    const shuf = shuffle(indexed);

    const payload = makeVraiFaux(
        shuf.map((x) => x.l),
        shuf.map((x) => x.br),
        `${RAPPEL_VERIF}<p>Pour chaque égalité, dis si elle exprime correctement une division euclidienne (elle est vraie <em>et</em> le reste est strictement inférieur au diviseur).</p>`,
        { ...opts(L_VF), colonnes: ['Vraie', 'Fausse'] },
    );
    payload._meta.generatorKey = 'vrai_faux_euclidienne';
    return payload;
};

const buildVFFallback = () => {
    // Triplets de secours, pré-vérifiés (2 vrais + 2 faux) :
    // V1 : 87 = 4 × 21 + 3      ✓ (3 < 4)
    // V2 : 295 = 18 × 16 + 7    ✓ (7 < 18)
    // F1 : 87 = 4 × 20 + 7      (vrai arithmétiquement mais 7 ≥ 4)
    // F2 : 116 = 7 × 17 + 4     (faux : 7×17+4 = 123 ≠ 116)
    const lignes = [
        'Division de 87 par 4 : 87 = 4 × 21 + 3',
        'Division de 295 par 18 : 295 = 18 × 16 + 7',
        'Division de 87 par 4 : 87 = 4 × 20 + 7',
        'Division de 116 par 7 : 116 = 7 × 17 + 4',
    ];
    const bonnesReponses = [0, 0, 1, 1];
    const payload = makeVraiFaux(
        lignes, bonnesReponses,
        `${RAPPEL_VERIF}<p>Pour chaque égalité, dis si elle exprime correctement une division euclidienne (elle est vraie <em>et</em> le reste est strictement inférieur au diviseur).</p>`,
        { ...opts(L_VF), colonnes: ['Vraie', 'Fausse'] },
    );
    payload._meta.generatorKey = 'vrai_faux_euclidienne';
    return payload;
};


// ═══════════════════════════════════════════════════════════
// EXERCICE 8 — traduire_division (QCM uniquement, revue 2026-08-26)
// Une division POSÉE (avec quotient et reste affichés) est montrée ; l'élève
// choisit l'égalité a = b × q + r qui la traduit. Réciproque de lecture_egalite.
// ═══════════════════════════════════════════════════════════

const L_TRADUIRE_QCM = 'Traduire une division posée en égalité (QCM)';

/**
 * Potence SVG statique (data URL, currentColor pour la compatibilité thème) :
 * dividende | diviseur, quotient sous le diviseur, reste en bas à gauche.
 */
const buildPoseeDivisionSvg = (a, b, q, r) => {
    const svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 230 130" width="230" height="130">'
        + '<g font-family="ui-monospace,monospace" font-size="20" font-weight="700" fill="currentColor">'
        + `<text x="96" y="38" text-anchor="end">${a}</text>`
        + '<line x1="110" y1="16" x2="110" y2="112" stroke="currentColor" stroke-width="1.8"/>'
        + '<line x1="110" y1="48" x2="216" y2="48" stroke="currentColor" stroke-width="1.8"/>'
        + `<text x="122" y="40">${b}</text>`
        + `<text x="122" y="80">${q}</text>`
        + `<text x="96" y="86" text-anchor="end">${r}</text>`
        + '</g>'
        + '<text x="96" y="106" text-anchor="end" font-family="system-ui,sans-serif" font-size="11" font-weight="600" fill="currentColor" opacity="0.65">reste</text>'
        + '<text x="150" y="104" font-family="system-ui,sans-serif" font-size="11" font-weight="600" fill="currentColor" opacity="0.65">quotient</text>'
        + '</svg>';
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/** Tirage : b ∈ [4, 19], reste ≥ 1 — 70 % de dividendes à 3 chiffres,
 *  30 % à 4 chiffres (revue 2026-08-26, choix prof). */
const pickTraduire = () => {
    const grand = Math.random() >= 0.7;
    const [aMin, aMax] = grand ? [1000, 9999] : [100, 999];
    for (let t = 0; t < 30; t++) {
        const b = randInt(4, 19);
        const q = randInt(Math.ceil(aMin / b), Math.floor((aMax - b) / b));
        const r = randInt(1, b - 1);
        const a = b * q + r;
        if (a < aMin || a > aMax) continue;
        if (isValidEucl(a, b, q, r)) return [a, b, q, r];
    }
    return [452, 13, 34, 10];
};

// Anti-répétition immédiate (recette standard).
let derniereSignatureTraduire618 = null;

const genTraduireQcm = () => {
    // Signature = l'IMAGE (le texte de ce format est constant, les nombres
    // vivent dans le SVG de la potence).
    const sig = (p) => p.elements_additionnels.elements[0].config.imageUrl;
    let payload = construireTraduireQcm();
    for (let t = 0; t < 8 && sig(payload) === derniereSignatureTraduire618; t++) {
        payload = construireTraduireQcm();
    }
    derniereSignatureTraduire618 = sig(payload);
    return payload;
};

const construireTraduireQcm = () => {
    const [a, b, q, r] = pickTraduire();
    const bonne = `${a} = ${b} × ${q} + ${r}`;

    // Familles d'erreur (toutes arithmétiquement fausses, vérifiées) :
    //  D1 : oubli du reste          a = b × q
    //  D2 : reste soustrait         a = b × q − r
    //  D3 : rôles quotient/reste échangés  a = b × r + q
    const candidats = [];
    candidats.push(`${a} = ${b} × ${q}`);                    // faux car r ≥ 1
    candidats.push(`${a} = ${b} × ${q} − ${r}`);             // faux (a − 2r ≠ a)
    if (b * r + q !== a) candidats.push(`${a} = ${b} × ${r} + ${q}`);

    const distracteurs = cleanDistracteurs(bonne, candidats, 3, (i) => {
        if (i === 0) return `${a} = ${b} × ${q + 1} + ${r}`;
        if (i === 1) return `${a} = ${q} × ${r} + ${b}`;
        return `${a} = ${b} + ${q} + ${r}`;
    });

    const elAdd = {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: buildPoseeDivisionSvg(a, b, q, r), maxWidth: 'small' } }],
    };
    const payload = makeQcm(
        [bonne, ...distracteurs],
        bonne,
        `<p>Voici une division posée, déjà effectuée.</p>{{element:0}}<p>Quelle égalité traduit correctement cette division ?</p>`,
        { ...opts(L_TRADUIRE_QCM), elements_additionnels: elAdd },
    );
    payload._meta.generatorKey = 'traduire_division';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    poser_diviseur_simple_qcm_unique:    genPoserSimpleQcm,
    poser_diviseur_simple_texte:         genPoserSimpleSaisie,
    poser_diviseur_2chiffres_qcm_unique: genPoser2chQcm,
    poser_diviseur_2chiffres_texte:      genPoser2chSaisie,
    lecture_egalite_qcm_unique:          genLectureQcm,
    lecture_egalite_texte:               genLectureSaisie,
    mixte_fraction_texte:                genMixteSaisie,
    encadrement_fraction_qcm_unique:     genEncadrementQcm,
    encadrement_fraction_texte:          genEncadrementSaisie,
    relier_divisions_relier:             genRelierDivisions,
    vrai_faux_euclidienne_tableau_vf:    genVraiFauxEuclidienne,
    traduire_division_qcm_unique:        genTraduireQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie',
    relier: 'Relier', tableau_vf: 'Vrai/Faux',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'poser_diviseur_simple',    label: L_POSER_SIMPLE_QCM,    formatsList: buildFormats('poser_diviseur_simple') },
    { id: 'poser_diviseur_2chiffres', label: L_POSER_2CHIFFRES_QCM, formatsList: buildFormats('poser_diviseur_2chiffres') },
    // Revue 2026-08-26 (choix prof) : le V/F « juger des égalités » rejoint la
    // catégorie lecture — même objet mathématique (a = b × q + r), deux
    // facettes (lire / contrôler) regroupées dans la même carte.
    {
        id: 'lecture_egalite',
        label: "Lecture et contrôle d'une égalité a = b×q + r",
        formatsList: [...buildFormats('lecture_egalite'), ...buildFormats('vrai_faux_euclidienne'), ...buildFormats('traduire_division')],
    },
    // Revue 2026-08-25 (choix prof, question A5) : fraction mixte et
    // encadrement MASQUÉS du catalogue — leur objet (écritures fractionnaires)
    // relève du chapitre fractions, pas de « effectuer la division
    // euclidienne ». Générateurs conservés dans GENERATORS : les questions
    // déjà en banque se régénèrent, et il suffit de décommenter pour rouvrir.
    // { id: 'mixte_fraction',           label: L_MIXTE,               formatsList: buildFormats('mixte_fraction') },
    // { id: 'encadrement_fraction',     label: L_ENCADREMENT_QCM,     formatsList: buildFormats('encadrement_fraction') },
    { id: 'relier_divisions',         label: L_RELIER,              formatsList: buildFormats('relier_divisions') },
];

// Exports utiles au stress test et autres usages internes
export const _internals = { divEucl, isValidEucl, ceilDiv, POOL_A_V, POOL_B_V, POOL_C_V, POOL_D_V, GENERATORS };

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) =>
            k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Toujours forcer la clé complète (les générateurs ne posent que le préfixe
    // catégorie ce qui cassait le "regenerate dans le même format")
    payload._meta.generatorKey = key;
    return payload;
};
